import React, { Component, Fragment } from 'react';
import PropTypes from "prop-types";
import { connect } from "react-redux";
import ClickNHold from 'react-click-n-hold';
import { isMobile } from 'react-device-detect';     // this npm is used to find out if the user is on mobile browser or not

import './Block.css';
import QuizBlock from './QuizBlock';
import { stopAudio } from '../../../helpers';
import { setCurrentPlayingId } from "../../../actions/actions";
import { development, production } from '../../../config';

let audioPath;
if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
    // dev code
    audioPath = development;
} else {
    // production code
    audioPath = production;
}

class Block extends Component {
    state = {
        isMoreInfoOpen: false,
        isAudioPlaying: false,
        audioDuration: 1,
        animationStatus: 'unset',
        disableShowMoreInfoToggle: false,
        blockAnswered: false,
    };

    blocks = [];
    selected = null;
    audioRef = React.createRef();
    clickTimeout = null;

    playAudioFile = () => {     // triggered when play button on moreInfoModal is clicked
        this.props.setCurrentPlayingId({ currentPlayingId: this.props.block.id, playOneBlockOnly: true });
        this.audioRef.current.play();
        this.setState({ animationStatus: 'running', isAudioPlaying: true });
    };
    pauseAudioFile = () => {    // // triggered when pause button on moreInfoModal is clicked
        if (this.state.isAudioPlaying) {
            this.audioRef.current.pause();
            this.setState({ animationStatus: 'paused', isAudioPlaying: false });
        }
    };
    adjustVolume = task => {    // triggered when volume buttons on moreInfoModal are clicked
        let volume = this.audioRef.current.volume;
        // the following if/else statements are used to make sure that the volume does not cross the set limit of [0.0, 1.0] or the app would crash
        if (volume === 1) {
            if (task === 'inc') volume = 1.0;
            else volume -= 0.1;
        } else if (volume > 0 && volume < 1.0) {
            if (task === 'inc') volume += 0.1;
            else volume -= 0.1;
        } else if (volume === 0) {
            if (task === 'inc') volume += 0.1;
            else volume = 0.0;
        }
        this.audioRef.current.volume = volume.toPrecision(1);
    };

    toggleMoreInfoModal = event => {
        event.stopPropagation();
        event.preventDefault();
        this.setState({
            isMoreInfoOpen: !this.state.isMoreInfoOpen
        }, () => {
            this.props.setMoreInfoStatus(this.state.isMoreInfoOpen);
            if (!this.state.isMoreInfoOpen && !this.props.currentPlayingId && !this.props.flashingStatus) this.props.resumeFlashing();
            else if (this.state.isMoreInfoOpen) this.props.stopFlashing();
        });
    };

    clicksHandler = blockHeld => {     // handles hold-to-select and both single/double clicks
        if (this.props.currentApp !== 'app-3' && (blockHeld || !!this.clickTimeout)) {    // for hold-to-select (blockHeld) AND double-clicks
            if (!this.state.isMoreInfoOpen && !this.props.isSequencePlaying) {   // because moreInfo appears on the same block, double-clicks on blocks that are showing moreInfo are disabled
                this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });
                this.setState({ isAudioPlaying: false });
                this.props.blockSelector(this.props.block, !this.props.isActive);   // this.props.block is the block that is double-clicked
            }
            clearTimeout(this.clickTimeout);
            this.clickTimeout = null;
        } else {    // for single-clicks
            this.clickTimeout = setTimeout(() => {
                if (this.props.currentApp !== 'app-3') {
                    let animationStatus = 'unset';
                    if (!this.state.isMoreInfoOpen && !this.props.isSequencePlaying && this.audioRef.current) {     // allowing the blocks to be played and paused only when sequence is not playing and moreInfo modal is not open
                        if (this.props.currentPlayingId && this.props.currentPlayingId !== this.props.block.id) {
                            this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });     // resetting the playing ID
                            this.setState({ isAudioPlaying: false });
                        }

                        if (this.state.isAudioPlaying) {    // If, for the same block, audio is already playing, pause it. Else play it
                            this.audioRef.current.pause();
                            animationStatus = 'paused';
                        } else {
                            this.props.setCurrentPlayingId({ currentPlayingId: this.props.block.id, playOneBlockOnly: true });
                            this.audioRef.current.play();
                            animationStatus = 'running';
                            this.props.stopFlashing();
                        }

                        this.setState({
                            animationStatus,
                            isAudioPlaying: !this.state.isAudioPlaying
                        });
                    }
                } else {
                    if (!this.state.isMoreInfoOpen) {
                        this.setState({ isMoreInfoOpen: !this.state.isMoreInfoOpen })
                    }
                }

                clearTimeout(this.clickTimeout);
                this.clickTimeout = null;
            }, 300) // 300 ms wait time to see if there was a second click
        }
    };

    onEnded = event => {    // this func runs when audio from either of the blocks ends
        event.preventDefault();
        if (this.props.playOneBlockOnly) this.props.setCurrentPlayingId({ currentPlayingId: null, playOneBlockOnly: true });    // if one block (not a sequence) is to be played
        else this.props.next();     // else run the next() func which lets the next selected block to play

        if (!this.props.flashingStatus && !this.state.isMoreInfoOpen && !this.props.moreInfoStatus && !this.props.scriptListStatus && !this.props.isSequencePlaying) {
            // isMoreInfo is for current block. moreInfoStatus is for all of the other blocks
            this.props.resumeFlashing();
        }
        this.setState({ animationStatus: 'unset', isAudioPlaying: false });
    };

    blockHeld = (e, enough) => { if (!!enough) this.clicksHandler(true) };      // e is event. enough gets a value if the block has been held for some time

    componentDidMount() {
        if (this.props.currentApp !== 'app-3') {
            if (this.audioRef.current) {
                this.audioRef.current.addEventListener('loadedmetadata', audio => {
                    this.setState({ audioDuration: (audio.target.duration * 1.0).toFixed(1) })
                });
                this.audioRef.current.volume = 0.7;     // 70% volume to start with
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps !== this.props) {
            if (this.props.currentApp !== 'app-3') {
                if (this.props.selectedBlocks && this.props.selectedBlocks.length === 5) {
                    if (this.props.block.id === this.props.currentPlayingId) {  // if it is this.props.block's turn to play the audio
                        if (this.props.isSequencePlaying) this.audioRef.current.play();     // so that we play it ONLY When it is to be played in a sequence. Else, the play/pause will be handled by clicksHandler() func
                    } else stopAudio(this.audioRef.current);
                } else if (this.props.block.id !== this.props.currentPlayingId) stopAudio(this.audioRef.current);   // stop all of the other blocks
            }
        }

        if (prevState !== this.state) {
            if (prevState.isMoreInfoOpen !== this.state.isMoreInfoOpen) {
                this.props.disableSlider(this.state.isMoreInfoOpen)
            }
        }
    }

    render() {
        const block = this.props.block;
        const flipped = this.state.isMoreInfoOpen ? ' flipped ' : '';
        const bgColor = `rgb(${this.props.color})`;
        const boxShadow = 'inset 4px 4px 12px rgba(0, 0, 0, .1), inset -4px -4px 12px #fff';
        let blockStyle = {
            background: `${flipped ? '#fff' : bgColor}`,
            border: `2px solid ${bgColor}`,
            boxShadow: `${flipped ? boxShadow : 'none'}`,
            zIndex: `${flipped ? 1000 : 50}`
        };

        if (this.props.currentApp === 'app-3') {
            blockStyle = { ...blockStyle, pointerEvents: !!this.state.blockAnswered ? 'none' : 'auto' };
        }

        const playingCSS = this.props.currentPlayingId === block.id ? ' playing ' : '';
        const hideUnSelectedBlock = (this.props.showActiveOnly && this.props.selectedBlocks && this.props.selectedBlocks.length === 5 && !this.props.isActive) ? ' hide ' : '';
        const animationStyles = { animationDuration: `${this.state.audioDuration}s`, animationPlayState: `${this.state.animationStatus}` };
        const buttonsDisabled = this.props.isSequencePlaying;   // for buttons that appear in moreInfo
        const audioButtonClass = buttonsDisabled ? 'audio-button-disabled' : `audio-button ${this.props.currentApp}`;
        let isPlaying = '', audioIsPlayed = '', flashingCSS = '', animation = '';
        let scriptDesc = false;
        let showDesc = (
            this.state.isMoreInfoOpen ? <span><i className="fa fa-remove" /></span> : <div><i className="fa fa-arrow-left" /></div>
        );

        if (this.props.isBeforeAfterFlashing) flashingCSS = ' before-after-sequence ';
        if (this.props.isFlashing) flashingCSS = ' active-sequence ';
        if (this.props.showActiveOnly) {
            showDesc = '';
            flashingCSS = '';
        }
        if (this.props.currentPlayingId || !this.props.flashingStatus) flashingCSS = '';
        if (this.props.selectedBlocks && !this.props.currentPlayingId) {
            isPlaying = '';
            audioIsPlayed = '';
        }
        if (this.state.animationStatus === 'running' || this.state.animationStatus === 'paused') {
            isPlaying = 'playing';
        } else isPlaying = '';
        if (this.props.showActiveOnly && this.props.isActive && this.props.selectedBlocks && this.props.selectedBlocks.length === 5) {
            if (this.props.sequenceIndex !== -1) animation = ` script-mode selected-${this.props.sequenceIndex + 1} `;
            scriptDesc = true;
        }
        if (this.props.currentPlayingId === block.id) {
            isPlaying = 'playing';
            audioIsPlayed = ' active-sequence ';
        } else isPlaying = '';

        const isActive = this.props.isActive ? ` selected ${flashingCSS} ` : '';

        let allCSS = this.props.css + flipped;
        if (this.props.currentApp !== 'app-3') allCSS = this.props.css + flipped + audioIsPlayed + isActive + animation + hideUnSelectedBlock;

        const showScript = (
            scriptDesc ? <div className={'script script-' + (this.props.sequenceIndex + 1) + playingCSS}>{block.script}</div> : null
        );
        const flipperSpan = (   // so that we don't show the flipper icon at all when the scripts are open (opening moreInfo while scripts are being displayed is not allowed)
            this.props.scriptListStatus ? null : <span className={`${isMobile ? 'flipper-icon-mobile': 'flipper-icon'}`} onClick={this.toggleMoreInfoModal}>{showDesc}</span>
        );
        const isPlayingDiv = (  // for the animation on top of the block that is currently playing
            isPlaying ? <div className={isPlaying} style={animationStyles} /> : null
        );
        const progressBar = (
            isPlaying ? <div className={`progress-bar ${this.props.currentApp}`} style={animationStyles} /> : null
        );

        let audioContainer = null;
        if (this.props.currentApp !== 'app-3') {
            let audioSrc = `${audioPath}/app-1/${block.audio}`;
            if (this.props.currentApp === 'app-2') audioSrc = `${audioPath}/app-2/T${this.props.selectedTopicId}-${block.audio}.mp3`;

            audioContainer = [
                <audio onEnded={this.onEnded} ref={this.audioRef} key={'audio'}>
                    <source src={audioSrc} type="audio/mpeg" />
                </audio>,
                <div className="progress-bar-container" key={'progress-bar'}>
                    <div className='progress'>
                        {progressBar}
                    </div>
                    <div>
                        <div onClick={this.playAudioFile} className={audioButtonClass}>
                            <i className="fa fa-play" />
                        </div>
                        <div onClick={this.pauseAudioFile} className={audioButtonClass}>
                            <i className="fa fa-pause" />
                        </div>
                        <div onClick={() => this.adjustVolume('dec')} className={audioButtonClass}>
                            <i className="fa fa-volume-down" />
                        </div>
                        <div onClick={() => this.adjustVolume('inc')} className={audioButtonClass}>
                            <i className="fa fa-volume-up" />
                        </div>
                    </div>
                </div>
            ];
        }

        let otherSideDiv = (
            <div className='content-details hide-other-side'>
                {audioContainer}
            </div>
        );

        if (this.state.isMoreInfoOpen) {
            if (this.props.currentApp !== 'app-3') {
                otherSideDiv = (
                    <div className='content-details show-other-side'>
                        <div className="mt-5">{block.script}</div>
                        {audioContainer}
                    </div>
                );
            } else {
                const style = { height: '100%', width: '100%' };
                otherSideDiv = (
                    <div className='content-details show-other-side' style={style}>
                        <QuizBlock
                            block={block}
                            quizMode={false}
                            difficultyLevel={this.props.difficultyLevel}
                            selectedTopic={this.props.selectedTopicId}
                            closeQuiz={() => this.setState({ isMoreInfoOpen: false })}
                            blockAnswered={val => this.setState({ blockAnswered: val }) }
                        />
                    </div>
                );
            }
        }

        const blockMark = (
            this.props.currentApp === 'app-3' && !!this.state.blockAnswered ?
                this.state.blockAnswered === 'correct' ? <div className='answer-mark'><i className='fa fa-check fa-fw' /></div>
            : <div className='answer-mark'><i className='fa fa-remove '/></div>
            : null
        );

        const blockDiv = (
            <div className={allCSS} style={blockStyle} onClick={() => this.clicksHandler(false)}>
                {otherSideDiv}
                {isPlayingDiv}
                {this.props.currentApp !== 'app-3' ? flipperSpan : null}
                {blockMark}
            </div>
        );

        const mobileBlockDiv = (
            // time -> time required to hold the block to select it (in seconds)
            // onCLickNHold -> runs when a block has been held for the specified amount of time
            <ClickNHold time={0.6} onClickNHold={this.blockHeld}>
                {blockDiv}
            </ClickNHold>
        );

        return (
            <Fragment>
                {isMobile ? mobileBlockDiv : blockDiv}
                {showScript}
            </Fragment>
        )
    }
}

Block.propTypes = {
    blockSelector: PropTypes.func,
    pauseAudio: PropTypes.func,
    onHandlePause: PropTypes.func,
    data: PropTypes.object,
    isActive: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isFlashing: PropTypes.bool,
    isBeforeAfterFlashing: PropTypes.bool
};

const mapStateToProps = state => ({
    currentApp: state.appReducer.currentApp,
    selectedTopicId: state.topicListReducer.selectedTopicId,
    currentPlayingId: state.touchPadReducer.currentPlayingId,
    playOneBlockOnly: state.touchPadReducer.playOneBlockOnly,
    selectedBlocks: state.touchPadReducer.selectedBlocks,
    showActiveOnly: state.touchPadReducer.showActiveOnly,
    selectedSeq: state.touchPadReducer.selectedSeq,
});

const mapDispatchToProps = dispatch => ( {
    setCurrentPlayingId: params => dispatch(setCurrentPlayingId(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Block);